import * as THREE from 'three';
import type { CharacterRig } from './types';

export type ColorMaterial = THREE.Material & { color: THREE.Color };
export type EmissiveMaterial = THREE.Material & { emissive: THREE.Color };

export function getRigRenderMeshes(rig: CharacterRig): THREE.Mesh[] {
  return uniqueMeshes(
    rig.renderMeshes != null && rig.renderMeshes.length > 0 ? rig.renderMeshes : [rig.bodyMesh],
  );
}

export function getRigColorableMeshes(rig: CharacterRig): THREE.Mesh[] {
  return uniqueMeshes(
    rig.colorableMeshes != null && rig.colorableMeshes.length > 0
      ? rig.colorableMeshes
      : getRigRenderMeshes(rig),
  );
}

export function getRigColorMaterials(rig: CharacterRig): ColorMaterial[] {
  return uniqueMaterials(getRigColorableMeshes(rig), isColorMaterial);
}

export function getRigEmissiveMaterials(rig: CharacterRig): EmissiveMaterial[] {
  return uniqueMaterials(getRigRenderMeshes(rig), isEmissiveMaterial);
}

export function forEachMeshMaterial(
  mesh: THREE.Mesh,
  visit: (material: THREE.Material) => void,
): void {
  const material = mesh.material;
  const materials = Array.isArray(material) ? material : [material];
  for (const mat of materials) visit(mat);
}

function uniqueMeshes(meshes: readonly (THREE.Mesh | null | undefined)[]): THREE.Mesh[] {
  const seen = new Set<THREE.Mesh>();
  const result: THREE.Mesh[] = [];
  for (const mesh of meshes) {
    if (!(mesh instanceof THREE.Mesh) || seen.has(mesh)) continue;
    seen.add(mesh);
    result.push(mesh);
  }
  return result;
}

function uniqueMaterials<T extends THREE.Material>(
  meshes: readonly THREE.Mesh[],
  guard: (material: THREE.Material) => material is T,
): T[] {
  const seen = new Set<THREE.Material>();
  const result: T[] = [];
  for (const mesh of meshes) {
    forEachMeshMaterial(mesh, (material) => {
      if (seen.has(material) || !guard(material)) return;
      seen.add(material);
      result.push(material);
    });
  }
  return result;
}

function isColorMaterial(material: THREE.Material): material is ColorMaterial {
  return 'color' in material && material.color instanceof THREE.Color;
}

function isEmissiveMaterial(material: THREE.Material): material is EmissiveMaterial {
  return 'emissive' in material && material.emissive instanceof THREE.Color;
}
